import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Switch,
  FormControlLabel,
  TextField,
  Button,
  Divider,
  Alert,
  Snackbar,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Slider,
  Chip
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SaveIcon from '@mui/icons-material/Save';
import AdminService from '../../services/AdminService';

const Settings = () => {
  const [generalSettings, setGeneralSettings] = useState({
    appName: 'Doc Quest',
    defaultTheme: 'light',
    maxUploadSize: 20,
    enableUserRegistration: true,
    sessionTimeout: 30
  });
  
  const [searchSettings, setSearchSettings] = useState({
    defaultModel: 'azure-openai',
    maxSearchResults: 10,
    enableContextualSearch: true,
    searchTimeout: 30,
    relevanceThreshold: 0.7
  });
  
  const [alertSettings, setAlertSettings] = useState({
    enableEmailAlerts: true,
    alertRecipients: 'admin@example.com',
    alertSeverityLevel: 'warning',
    dailyDigest: true
  });
  
  const [loading, setLoading] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [error, setError] = useState(null);
  
  // This would typically fetch settings from the API
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        setLoading(true);
        
        // In a real application, this would be fetched from an API
        // For demonstration, we're using the state defaults
        
        setLoading(false);
      } catch (err) {
        console.error('Error fetching settings:', err);
        setError('Failed to load settings. Please try again.');
        setLoading(false);
      }
    };
    
    fetchSettings();
  }, []);
  
  const handleGeneralSettingsChange = (event) => {
    const { name, value, checked } = event.target;
    const newValue = event.target.type === 'checkbox' ? checked : value;
    
    setGeneralSettings(prev => ({
      ...prev,
      [name]: newValue
    }));
  };
  
  const handleSearchSettingsChange = (event) => {
    const { name, value, checked } = event.target;
    const newValue = event.target.type === 'checkbox' ? checked : value;
    
    setSearchSettings(prev => ({
      ...prev,
      [name]: newValue
    }));
  };
  
  const handleAlertSettingsChange = (event) => {
    const { name, value, checked } = event.target;
    const newValue = event.target.type === 'checkbox' ? checked : value;
    
    setAlertSettings(prev => ({
      ...prev,
      [name]: newValue
    }));
  };
  
  const handleSliderChange = (setting, newValue) => {
    setSearchSettings(prev => ({
      ...prev,
      [setting]: newValue
    }));
  };
  
  const handleSaveSettings = async () => {
    try {
      setLoading(true);
      
      // In a real application, this would save settings to an API
      await new Promise(resolve => setTimeout(resolve, 1000)); // Simulate API call
      
      setShowSuccess(true);
      setError(null);
    } catch (err) {
      console.error('Error saving settings:', err);
      setError('Failed to save settings. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  
  const handleCloseSnackbar = () => {
    setShowSuccess(false);
  };
  
  return (
    <Box>
      <Typography variant="h5" gutterBottom>System Settings</Typography>
      
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}
      
      <Accordion defaultExpanded>
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          aria-controls="general-settings-content"
          id="general-settings-header"
        >
          <Typography variant="h6">General Settings</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Application Name"
                name="appName"
                value={generalSettings.appName}
                onChange={handleGeneralSettingsChange}
                margin="normal"
                variant="outlined"
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <FormControl fullWidth margin="normal">
                <InputLabel>Default Theme</InputLabel>
                <Select
                  name="defaultTheme"
                  value={generalSettings.defaultTheme}
                  onChange={handleGeneralSettingsChange}
                  label="Default Theme"
                >
                  <MenuItem value="light">Light</MenuItem>
                  <MenuItem value="dark">Dark</MenuItem>
                  <MenuItem value="system">System Default</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Max Upload Size (MB)"
                name="maxUploadSize"
                type="number"
                value={generalSettings.maxUploadSize}
                onChange={handleGeneralSettingsChange}
                margin="normal"
                variant="outlined"
                InputProps={{ inputProps: { min: 1, max: 100 } }}
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Session Timeout (minutes)"
                name="sessionTimeout"
                type="number"
                value={generalSettings.sessionTimeout}
                onChange={handleGeneralSettingsChange}
                margin="normal"
                variant="outlined"
                InputProps={{ inputProps: { min: 1, max: 240 } }}
              />
            </Grid>
            
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    name="enableUserRegistration"
                    checked={generalSettings.enableUserRegistration}
                    onChange={handleGeneralSettingsChange}
                    color="primary"
                  />
                }
                label="Enable User Registration"
              />
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>
      
      <Accordion>
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          aria-controls="search-settings-content"
          id="search-settings-header"
        >
          <Typography variant="h6">Search Settings</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth margin="normal">
                <InputLabel>Default Model</InputLabel>
                <Select
                  name="defaultModel"
                  value={searchSettings.defaultModel}
                  onChange={handleSearchSettingsChange}
                  label="Default Model"
                >
                  <MenuItem value="azure-openai">Azure OpenAI</MenuItem>
                  <MenuItem value="openai">OpenAI</MenuItem>
                  <MenuItem value="gemini">Google Gemini</MenuItem>
                  <MenuItem value="anthropic">Anthropic Claude</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Max Search Results"
                name="maxSearchResults"
                type="number"
                value={searchSettings.maxSearchResults}
                onChange={handleSearchSettingsChange}
                margin="normal"
                variant="outlined"
                InputProps={{ inputProps: { min: 1, max: 100 } }}
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Search Timeout (seconds)"
                name="searchTimeout"
                type="number"
                value={searchSettings.searchTimeout}
                onChange={handleSearchSettingsChange}
                margin="normal"
                variant="outlined"
                InputProps={{ inputProps: { min: 5, max: 120 } }}
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Box sx={{ mt: 3 }}>
                <Typography gutterBottom>Relevance Threshold</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Slider
                    value={searchSettings.relevanceThreshold}
                    onChange={(e, newValue) => handleSliderChange('relevanceThreshold', newValue)}
                    step={0.1}
                    marks
                    min={0}
                    max={1}
                    valueLabelDisplay="auto"
                    valueLabelFormat={value => `${value * 100}%`}
                    sx={{ flexGrow: 1, mr: 2 }}
                  />
                  <Chip label={`${searchSettings.relevanceThreshold * 100}%`} />
                </Box>
              </Box>
            </Grid>
            
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    name="enableContextualSearch"
                    checked={searchSettings.enableContextualSearch}
                    onChange={handleSearchSettingsChange}
                    color="primary"
                  />
                }
                label="Enable Contextual Search"
              />
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>
      
      <Accordion>
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          aria-controls="alert-settings-content"
          id="alert-settings-header"
        >
          <Typography variant="h6">Alert Settings</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    name="enableEmailAlerts"
                    checked={alertSettings.enableEmailAlerts}
                    onChange={handleAlertSettingsChange}
                    color="primary"
                  />
                }
                label="Enable Email Alerts"
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Alert Recipients"
                name="alertRecipients"
                value={alertSettings.alertRecipients}
                onChange={handleAlertSettingsChange}
                margin="normal"
                variant="outlined"
                placeholder="Comma-separated email addresses"
                disabled={!alertSettings.enableEmailAlerts}
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <FormControl fullWidth margin="normal" disabled={!alertSettings.enableEmailAlerts}>
                <InputLabel>Alert Severity Level</InputLabel>
                <Select
                  name="alertSeverityLevel"
                  value={alertSettings.alertSeverityLevel}
                  onChange={handleAlertSettingsChange}
                  label="Alert Severity Level"
                >
                  <MenuItem value="critical">Critical Only</MenuItem>
                  <MenuItem value="warning">Warning & Critical</MenuItem>
                  <MenuItem value="info">All (Info, Warning, Critical)</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    name="dailyDigest"
                    checked={alertSettings.dailyDigest}
                    onChange={handleAlertSettingsChange}
                    color="primary"
                    disabled={!alertSettings.enableEmailAlerts}
                  />
                }
                label="Send Daily Digest Email"
              />
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>
      
      <Box sx={{ mt: 4, display: 'flex', justifyContent: 'flex-end' }}>
        <Button
          variant="contained"
          color="primary"
          startIcon={<SaveIcon />}
          onClick={handleSaveSettings}
          disabled={loading}
        >
          {loading ? 'Saving...' : 'Save Settings'}
        </Button>
      </Box>
      
      <Snackbar
        open={showSuccess}
        autoHideDuration={5000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity="success">
          Settings saved successfully!
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Settings;